import React, { useState, useEffect, useRef, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { Smartphone, X, RefreshCw, CheckCircle, Loader2, QrCode } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const APP_URL = typeof window !== 'undefined' ? window.location.origin : '';
const POLL_INTERVAL = 2500;

const PhotoBridge = ({ onPhotosReceived }) => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [creating, setCreating] = useState(false);
  const pollRef = useRef(null);
  const knownCountRef = useRef(0);

  const createSession = useCallback(async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/api/photo-bridge/session`);
      setSessionId(res.data.session_id);
      setPhotos([]);
      knownCountRef.current = 0;
    } catch {
      // silently fail
    } finally {
      setCreating(false);
    }
  }, []);

  const handleOpen = () => {
    setOpen(true);
    createSession();
  };

  const handleClose = () => {
    setOpen(false);
    if (pollRef.current) clearInterval(pollRef.current);
    // Send all accumulated photos to parent
    if (photos.length > 0) {
      onPhotosReceived(photos);
    }
  };

  // Poll for new photos
  useEffect(() => {
    if (!open || !sessionId) return;

    const poll = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/photo-bridge/${sessionId}/photos`);
        const all = res.data.photos || [];
        if (all.length > knownCountRef.current) {
          setPhotos(all);
          knownCountRef.current = all.length;
        }
      } catch {
        // session expired or error
      }
    };

    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [open, sessionId]);

  const bridgeUrl = sessionId ? `${APP_URL}/photo-bridge/${sessionId}` : '';

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className="w-full border-2 border-dashed border-[#E07A3A]/30 hover:border-[#E07A3A] bg-[#FEF3EC]/30 p-4 flex items-center justify-center gap-3 transition-colors group"
        data-testid="photo-bridge-btn"
      >
        <Smartphone className="h-5 w-5 text-[#E07A3A] group-hover:scale-110 transition-transform" />
        <span className="text-[#E07A3A] text-sm font-semibold tracking-wider">Upload from Another Phone</span>
        <QrCode className="h-4 w-4 text-[#E07A3A]/50" />
      </button>

      <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
        <DialogContent className="bg-white border-slate-200 max-w-md" data-testid="photo-bridge-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1A1A1A]">
              <Smartphone className="h-5 w-5 text-[#E07A3A]" />
              Photo Bridge
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Scan the QR code with your other phone to send photos here
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-5">
            {/* QR Code */}
            {creating ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-8 w-8 text-[#E07A3A] animate-spin" />
              </div>
            ) : sessionId ? (
              <div className="flex flex-col items-center gap-4">
                <div className="p-4 bg-white border-2 border-slate-100 shadow-sm" data-testid="photo-bridge-qr">
                  <QRCodeSVG
                    value={bridgeUrl}
                    size={200}
                    level="M"
                    fgColor="#0F172A"
                    bgColor="#FFFFFF"
                  />
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-400 tracking-wider">SCAN WITH YOUR PHONE CAMERA</p>
                  <p className="text-[10px] text-slate-300 mt-1 break-all max-w-[280px]">{bridgeUrl}</p>
                </div>
              </div>
            ) : null}

            {/* Steps */}
            <div className="bg-[#F8FAFC] p-4 space-y-2">
              {[
                'Open camera on your other phone',
                'Scan the QR code above',
                'Take photos or choose from gallery',
                'Photos appear here automatically',
              ].map((step, i) => (
                <div key={step} className="flex items-center gap-3">
                  <span className="w-6 h-6 flex-shrink-0 flex items-center justify-center bg-[#E07A3A]/10 text-[#E07A3A] text-xs font-bold">{i + 1}</span>
                  <span className="text-slate-600 text-sm">{step}</span>
                </div>
              ))}
            </div>

            {/* Incoming photos */}
            {photos.length > 0 ? (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-green-600 text-sm font-semibold">{photos.length} photo{photos.length !== 1 ? 's' : ''} received</span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {photos.map((url) => (
                    <div key={url} className="aspect-square bg-slate-100 overflow-hidden">
                      <img src={url.startsWith('/') ? `${API_URL}${url}` : url} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            ) : sessionId ? (
              <div className="flex items-center justify-center gap-2 py-3 text-slate-400">
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span className="text-sm">Waiting for photos from phone...</span>
              </div>
            ) : null}

            {/* Done button */}
            <Button
              onClick={handleClose}
              className="w-full bg-[#E07A3A] text-white hover:bg-[#C4652E] py-5 tracking-wider font-semibold"
              data-testid="photo-bridge-done-btn"
            >
              {photos.length > 0 ? `Add ${photos.length} Photo${photos.length !== 1 ? 's' : ''} to Listing` : 'Close'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PhotoBridge;
